"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Vote,
  Sparkles,
  Building2,
  Crown,
  Scale,
  Users,
  ShieldCheck,
  Eye,
  Radio,
  Activity,
  Award,
  ArrowRight,
  Mic,
  Compass,
  Megaphone,
  AlertTriangle,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const HEADLINE_STATS = [
  { metric: "15", suffix: "/18", label: "Victories", sub: "Verified electoral outcomes", accent: "cyan" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "Twenty-two languages", accent: "purple" },
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "emerald" },
  { metric: "87", suffix: "%", label: "Booth-Level Accuracy", sub: "Field Intelligence Layer", accent: "amber-gold" },
  { metric: "0", suffix: "", label: "Security Breaches", sub: "Fifteen-plus years", accent: "red" },
  { metric: "60", suffix: "min", label: "Decision Time", sub: "From 24-72 hours", accent: "denim" },
];

const FAILURES = [
  { num: "01", title: "Inaccurate Voter Base Understanding", body: "Most campaigns operate without accurate understanding of voter demographics, sentiments, needs, issues, opinions, alignments, capacity, perception, and knowledge. Generic messages broadcast to diverse populations, wasted resources on voters who were never persuadable.", accent: "red" },
  { num: "02", title: "Limited Official Channel Reach", body: "Official channels — party social media, press releases, broadcast appearances — have inherently limited reach, acceptability, impact, and believability. The true determinant is perception through unofficial channels: opinions, buzz, hearsay, and trends.", accent: "purple" },
  { num: "03", title: "Inability to Control Information Visibility", body: "What voters see across search media, social media, forums, news channels, comments, reviews, and trends is not determined by the campaign. Without systematic control over information visibility, the narrative is lost to competitors and adversaries.", accent: "cyan" },
  { num: "04", title: "Inadequate Negative Information Management", body: "Amplifying positive content is necessary but insufficient. Negative media, negative press, negative information circulating across the internet require active management — from root-cause suppression to rapid response to reputation recovery.", accent: "emerald" },
  { num: "05", title: "No Bulk Content Syndication", body: "Effective political communication requires publishing thousands of articles, news segments, and content pieces across the internet at scale — achieving headline dominance, search positioning, and competitive overwhelm.", accent: "amber-gold" },
  { num: "06", title: "Missing Ground-Level Intelligence", body: "Digital intelligence is necessary but insufficient. Direct ground-level engagement — talking to voters, understanding how they will vote, determining what it would take for them to vote favorably — requires physical presence at booth level.", accent: "denim" },
  { num: "07", title: "No Governance Delivery After Victory", body: "Winning an election without the infrastructure to deliver on promises creates a gap between expectation and reality that erodes trust and makes re-election impossible. Governance preparation must begin during the campaign, not after election day.", accent: "coral" },
];

const PILLARS = [
  { icon: Eye, title: "Perception, Reputation & Public Relations", body: "For political organizations, the Perception pillar manages the total information environment — what voters hear, see, and believe from all sources. The DMRE Cycle deploys four strategic modes: Promotion, Neutralization, Demotion, Transformation.", accent: "red" },
  { icon: ShieldCheck, title: "Privacy, Encryption & Information Security", body: "Campaign communications, internal strategy documents, donor data, and voter intelligence must remain protected throughout the campaign lifecycle. Zero-trust architecture ensures no adversary gains access to operational communications.", accent: "purple" },
  { icon: Vote, title: "Politics, Elections & Campaigning", body: "The political consulting capability refined through nearly a decade of international campaign experience. Booting booth-level intelligence with ninety-five to ninety-eight percent organic authenticity narrative deployment across fifty-plus digital platforms.", accent: "cyan" },
  { icon: Radio, title: "Policing, Intelligence & Defense", body: "Threat detection, coordinated narrative attacks, foreign interference, and domestic disruption — all identified and neutralized before they reach critical mass. Real-time monitoring across two hundred-plus digital platforms.", accent: "emerald" },
  { icon: Scale, title: "Policy, Governance & Transformation", body: "The transition from campaign to governance begins before election day. Policy formation, public scheme design, and implementation planning ensure that electoral promises convert into verified delivery. The architecture that wins elections also governs them.", accent: "amber-gold" },
];

const METHODOLOGY = [
  { num: "01", title: "Intelligence", body: "Ground-truth voter intelligence through booth-level field operatives combined with multi-source digital monitoring. Comprehensive voter landscape, sentiment map, and predictive forecasts — delivered with quantified confidence scores." },
  { num: "02", title: "Analysis", body: "Machine learning models detect statistical anomalies and causal relationships across disparate data streams. Voter micro-segments identified, swing voter dynamics mapped, persuasion pathways modeled with probabilistic outcomes." },
  { num: "03", title: "Strategy", body: "The Penta P framework deployed. Each pillar receives its specific role. Each platform receives its activation sequence. Resource architecture, timeline sequencing, and escalation protocols defined at granular level." },
  { num: "04", title: "Orchestration", body: "Operations Coordination Layer coordinates all platforms simultaneously. Decision time compressed from twenty-four to seventy-two hours to under one hour. Multi-disciplinary teams operate in unison through compartmentalized workflows." },
  { num: "05", title: "Amplification", body: "Content and narrative deploy at scale. Narrative Engineering Layer engineers narrative penetration across fifty-plus platforms with ninety-five to ninety-eight percent organic authenticity. Distribution Amplification Layer activates one thousand-plus syndication points." },
  { num: "06", title: "Feedback", body: "Every output is monitored, measured, and fed back into the Intelligence stage. Machine learning models retrain continuously. When strategy adjustments are needed, Operations Coordination Layer enables coordinated pivots across all platforms simultaneously." },
];

const OUTCOMES = [
  { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "Fifteen victories / eighteen engagements", accent: "cyan" },
  { metric: "900", suffix: "M+", label: "Voters Reached", sub: "Continental reach", accent: "purple" },
  { metric: "87", suffix: "%", label: "Booth Accuracy", sub: "Ground-truth validation", accent: "emerald" },
  { metric: "95", suffix: "%", label: "Organic Authenticity", sub: "Narrative deployment", accent: "amber-gold" },
  { metric: "1,000", suffix: "+", label: "Syndication Points", sub: "Content distribution", accent: "red" },
  { metric: "100", suffix: "%", label: "Verified Outcomes", sub: "Every engagement", accent: "denim" },
];

const CLIENTS = [
  { title: "National Political Parties", body: "Ruling parties, opposition parties, and emerging political movements seeking voter intelligence, campaign engineering, narrative control, and verified electoral outcomes at national scale.", accent: "cyan" },
  { title: "Presidential Candidates", body: "Individual candidates and their campaign operations requiring comprehensive support from intelligence through orchestration to election day operations and post-election governance preparation.", accent: "purple" },
  { title: "Parliamentary Candidates", body: "Constituency-level campaigns requiring booth-level intelligence, micro-segment voter mapping, local narrative deployment, and ground-truth validation against digital signals.", accent: "emerald" },
  { title: "Coalition Partners", body: "Multi-party coalitions and political alliances requiring coordinated strategy, unified messaging architecture, joint voter intelligence, and synchronized campaign operations.", accent: "amber-gold" },
  { title: "Political Action Committees", body: "Independent expenditure organizations and advocacy groups requiring voter targeting, narrative deployment, donor privacy protection, and outcome verification across jurisdictions.", accent: "red" },
  { title: "International Political Movements", body: "Transnational political organizations operating across borders requiring multi-jurisdictional compliance, cross-cultural narrative adaptation, and coordinated international campaign infrastructure.", accent: "denim" },
];

export default function PoliticalOrganizationsPage() {
  return (
    <>
      <PageHero
        icon={Vote}
        overline="Political Organizations"
        overlineClassName="text-cyan"
        title={
          <>
            Engineering Electoral <span className="text-gradient-premium">Outcomes at Sovereign Scale.</span>
          </>
        }
        subtitle="Political organizations do not engage Dr. Jyoti Kush for advice. They engage for outcomes. The eighty-three point three percent campaign success rate — fifteen victories in eighteen engagements — is not a projection. It is a verified operational record forged through nearly a decade of operating at booth level."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Sectors", href: "/sectors/" }, { label: "Political Organizations" }]}
        badge="Fifteen victories. Verified outcomes."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">Executive Summary</span>
            </div>
            <h2 className="text-display-md mb-5">
              The most comprehensive political engagement model in existence.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The Political Organizations sector represents the most comprehensive engagement model — the only domain where all five pillars of the Penta P Framework activate simultaneously. This is not coincidence. Elections are the most complex strategic environment on earth: multiple competing narratives, millions of voter micro-segments, real-time crisis exposure, ground-level operations, secure communications, and governance preparation all converging in a compressed timeline.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Dr. Jyoti Kush oversees the operational architecture that transforms this complexity into coordinated action. Under her command, the neural command interface orchestrates the platform ecosystem operating in real time — gathering intelligence from booth-level ground teams, deploying narratives across fifty-plus digital platforms with ninety-five to ninety-eight percent organic authenticity, syndicating content through one thousand-plus distribution points, neutralizing crises in minutes, and compressing decision-to-action time from twenty-four to seventy-two hours to under one hour.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The political consulting capability is not a side offering. It is the crucible in which the platforms, methodology, and operational discipline were forged. Every platform was built to solve a political challenge. Every methodology was refined through electoral operations. The Penta P Framework exists because political campaigns demanded the simultaneous activation of perception, privacy, politics, policing, and policy — and no existing organization could deliver it.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {HEADLINE_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ACCENT[s.accent].bg} to-transparent border ${ACCENT[s.accent].border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${ACCENT[s.accent].text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <AlertTriangle className="size-3.5 text-red" />
            <span className="text-overline text-red">The Political Challenge</span>
          </div>
          <h2 className="text-display-md mb-4">Seven failures of conventional political operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Elections are the highest-stakes strategic environment on earth. Conventional political consulting fails because it treats intelligence, narrative, ground operations, crisis management, secure communications, and governance preparation as separate disciplines, managed by separate teams, using separate tools, with no unified command.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FAILURES.slice(0, 6).map((f, i) => {
            const a = ACCENT[f.accent];
            return (
              <motion.div
                key={f.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{f.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{f.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-4 max-w-2xl mx-auto"
        >
          <div className="group relative glass-card rounded-2xl p-6 bg-gradient-to-br from-coral/10 to-transparent border border-coral/30 overflow-hidden">
            <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
            <div className="relative">
              <div className="text-display-sm font-bold text-coral opacity-30 leading-none mb-3">07</div>
              <h3 className="text-heading-md mb-3 text-coral">{FAILURES[6].title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed">{FAILURES[6].body}</p>
            </div>
          </div>
        </motion.div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Megaphone className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Five Pillars — Full Activation</span>
          </div>
          <h2 className="text-display-md mb-4">Perception, Privacy, Politics, Policing, Policy.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Political organizations represent one of only two sectors — alongside Government and Sovereign — where all five pillars activate in full. The most comprehensive engagement model in the portfolio, drawing from every capability simultaneously.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Activity className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Six-Stage Methodology</span>
            </div>
            <h2 className="text-display-md mb-5">Battle-tested operating system for electoral outcomes.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Every political engagement follows a six-stage methodology refined through hundreds of electoral operations. The stages are sequential in logic but iterative in practice. Intelligence flows continuously through all stages, and feedback from later stages constantly refines earlier ones.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The methodology closes a loop that never breaks. The system that enters the next engagement is smarter than the one that entered the last. Every outcome — win or loss — adds to the training corpus. Every cycle produces more precise intelligence than the one before.
            </p>
          </div>
          <div className="space-y-3">
            {METHODOLOGY.map((s, i) => {
              const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
              const a = ACCENT[accents[i]];
              return (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring}`}>
                      <span className="text-body-md font-bold text-white">{s.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Award className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">The verified record of political outcomes.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric below traces to operational data across fifteen-plus years of political engagements. These are not projections. Not targets. Not estimates. These are results — verified, operational, and drawn from engagements where the margin for error is measured in electoral outcomes.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {OUTCOMES.map((o, i) => {
            const a = ACCENT[o.accent];
            return (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} text-center`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>{o.metric}{o.suffix}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{o.label}</div>
                <div className="text-caption text-muted-foreground">{o.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from this engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Political organizations, candidates, and movements that measure success by electoral outcomes — not by media impressions, advertising spend, or generic reach metrics. The verified record speaks through victories, not promises.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{c.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <span className="text-overline text-cyan">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Political Strategy Ladder.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six sequential stages refined through nearly a decade of international campaign experience. Each stage produces outputs that feed and refine every other. The system that enters the next engagement is smarter than the one that entered the last.
          </p>
        </div>
        <StepperBlock
          steps={[
            { num: "01", title: "Intelligence", body: "Before strategy forms, the complete operational landscape is interrogated. CLAIRVOYANCE CX activates across 200+ platforms. TERRAFORM-IQ deploys field operatives. Output: intelligence baseline, threat landscape, opportunity map, and predictive forecasts with quantified confidence scores.", icon: <Eye className="size-4" />, accent: "cyan" },
            { num: "02", title: "Analysis", body: "Machine learning models detect statistical anomalies and causal relationships across disparate data streams. Voter micro-segments identified, swing voter dynamics mapped, persuasion pathways modeled with probabilistic outcomes and evidence-based recommendations.", icon: <Activity className="size-4" />, accent: "purple" },
            { num: "03", title: "Strategy", body: "The full Penta P framework deploys. Each pillar receives its specific role. Each platform receives its activation sequence. Resource architecture, timeline sequencing, and escalation protocols defined at granular level.", icon: <Megaphone className="size-4" />, accent: "emerald" },
            { num: "04", title: "Orchestration", body: "LITHVIK N1 activates as the central nervous system, coordinating all platforms simultaneously. Decision time compresses from 24-72 hours to under 1 hour. Multi-disciplinary teams operate in unison through compartmentalized workflows.", icon: <Radio className="size-4" />, accent: "amber-gold" },
            { num: "05", title: "Amplification", body: "Content and narrative deploy at scale. PERCEPTION X2 engineers narrative penetration across 50+ platforms with 95-98% organic authenticity. RICOCHET CATALYST X activates 1,000+ syndication points for content domination.", icon: <Sparkles className="size-4" />, accent: "red" },
            { num: "06", title: "Feedback", body: "Every output is monitored, measured, and fed back into the Intelligence stage. Machine learning models retrain continuously. When adjustments are needed, LITHVIK N1 enables coordinated pivots across all platforms simultaneously.", icon: <Compass className="size-4" />, accent: "denim" },
          ]}
          orientation="vertical"
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <span className="text-overline text-amber-gold">Electoral Record</span>
          </div>
          <h2 className="text-display-md mb-4">Verified outcomes from the political consulting record.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The metrics that follow are the most comprehensive verified electoral record in the organization's class. Every number traces to operational data, not projections.
          </p>
        </div>
        <CounterBlock
          columns={3}
          stats={[
            { value: 83, suffix: "%", label: "Campaign Success", description: "15 victories in 18 engagements", accent: "cyan" },
            { value: 900, suffix: "M+", label: "Voters Reached", description: "Continental electoral reach", accent: "purple" },
            { value: 87, suffix: "%", label: "Booth Accuracy", description: "TERRAFORM-IQ ground-truth validation", accent: "emerald" },
            { value: 95, suffix: "%", label: "Organic Authenticity", description: "PERCEPTION X2 narrative deployment", accent: "amber-gold" },
            { value: 1000, suffix: "+", label: "Syndication Points", description: "RICOCHET CATALYST X content distribution", accent: "red" },
            { value: 95, suffix: "%", label: "Coordination Success", description: "LITHVIK N1 neural command", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <span className="text-overline text-emerald">War Room Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Seven functional zones of the campaign nerve center.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The War Room is the centralized operational environment where LITHVIK N1 synthesizes intelligence from all platforms into a single command view — real-time intelligence streaming, multi-team coordination, and instant response capability.
          </p>
        </div>
        <FeatureGridBlock
          columns={4}
          features={[
            { title: "Intelligence Hub", body: "Real-time data aggregation from all platforms. CLAIRVOYANCE CX feeds digital intelligence. TERRAFORM-IQ feeds ground truth. Situation reports updated every 60 seconds.", icon: <Eye className="size-5" />, accent: "red" },
            { title: "Command Center", body: "Decision-making protocols, resource allocation authority, escalation management. Decision time compressed from 24-72 hours to under 1 hour.", icon: <Compass className="size-5" />, accent: "purple" },
            { title: "Content Operations", body: "Creation-approval-deployment pipeline from conception to publication in minutes. PERCEPTION X2 handles narrative, RICOCHET CATALYST X handles syndication.", icon: <Megaphone className="size-5" />, accent: "cyan" },
            { title: "Digital Operations", body: "Social media management, amplification orchestration, platform-specific optimization, BOT Army command through unofficial channels.", icon: <Activity className="size-5" />, accent: "emerald" },
            { title: "Ground Operations", body: "Field team coordination, volunteer management, event logistics, booth-level activity tracking through official and unofficial tracks.", icon: <Users className="size-5" />, accent: "amber-gold" },
            { title: "Crisis Response", body: "Negative content neutralization, rapid narrative counter-deployment, stakeholder notification. PHOENIX-1 provides 15-minute crisis response.", icon: <AlertTriangle className="size-5" />, accent: "red" },
            { title: "Security Operations", body: "Communications security monitoring, data protection enforcement, operational security maintenance, counter-surveillance for NINJA doctrine compliance.", icon: <ShieldCheck className="size-5" />, accent: "denim" },
            { title: "Voter Enticement", body: "Seven areas covering segmentation, profiling, perception management, geographic targeting, social equation management, and voter contact optimization.", icon: <Vote className="size-5" />, accent: "lime" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-cyan mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage Dr. Jyoti Kush.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Political organizations engage this capability for outcomes, not deliverables. Six-stage methodology. Fifteen victories in eighteen engagements. National security level confidentiality from first contact. The record is the standard.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/sectors/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Compass className="size-4" />
                All Sectors
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Governments", description: "Governance transformation at sovereign scale.", href: "/sectors/governments/", icon: Building2, accent: "red" },
          { title: "Presidential & PM Offices", description: "Strategic command at the highest level.", href: "/sectors/presidential-pm-offices/", icon: Crown, accent: "amber-gold" },
          { title: "Public Figures & HNWIs", description: "Reputation for prominent individuals.", href: "/sectors/public-figures-hnwis/", icon: Users, accent: "coral" },
          { title: "Sectors Hub", description: "Eight sectors. One operating system.", href: "/sectors/", icon: Vote, accent: "cyan" },
        ]}
      />
    </>
  );
}
